if (!customElements.get('watches-icons')) {
  customElements.define('watches-icons', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.faces = [...this.querySelectorAll('.wtFace')];
      this.items = [...this.querySelectorAll('.wtItem')];
      this.cores = [...this.querySelectorAll('.wtCore__i')];
      this.clock = this.querySelector('.wtClock');
      this.live = this.querySelector('.wi-live');
      this.current = -1;
      this.displayAngle = 0;
      this.lastFrameTime = null;
      this.keyboardNavigation = false;
      this.motion = matchMedia('(prefers-reduced-motion:reduce)');
      this.show = (index, announce = false) => {
        if (!this.faces.length) return;
        index = Math.max(0, Math.min(index, this.faces.length - 1));
        if (index === this.current) return;
        this.current = index;
        this.faces.forEach((face, i) => face.setAttribute('aria-pressed', String(i === index)));
        this.cores.forEach((core, i) => core.classList.toggle('is-on', i === index));
        this.items.forEach((item, i) => {
          item.classList.toggle('is-on', i === index);
          item.inert = i !== index;
          item.setAttribute('aria-hidden', String(i !== index));
        });
        if (announce) this.live.textContent = `${this.faces[index].getAttribute('aria-label')}, ${index + 1} of ${this.faces.length}`;
      };
      this.hand = angle => {
        this.clock.style.setProperty('--A', `${angle}deg`);
        this.clock.style.setProperty('--Adeg', Math.max(.01, angle));
      };
      this.select = (index, moveScroll = true) => {
        this.show(index, true);
        this.displayAngle = index * 360 / this.faces.length;
        this.hand(this.displayAngle);
        if (this.scrolling && moveScroll) {
          const runway = this.querySelector('.wtDial__run').offsetHeight;
          const fraction = index / Math.max(1, this.faces.length - 1);
          window.scrollTo({top: this.getBoundingClientRect().top + window.scrollY - (this.stickyTop || 0) + runway * fraction, behavior: 'instant'});
        }
      };
      this.update = now => {
        this.frame = null;
        if (!this.scrolling) return;
        // Keep focused product links stable while the user navigates with the keyboard.

        const runway = this.querySelector('.wtDial__run').offsetHeight;
        const progress = Math.min(1, Math.max(0, ((this.stickyTop || 0) - this.getBoundingClientRect().top) / Math.max(1, runway)));
        const position = progress * (this.faces.length - 1);
        // Hold exactly on a watch within 10% of the distance to either neighbour.
        // Outside this zone, remap continuously so entering/leaving never jumps.
        const whole = Math.floor(position);
        const fraction = position - whole;
        const snapZone = .1;
        let snappedPosition;
        if (fraction <= snapZone) snappedPosition = whole;
        else if (fraction >= 1 - snapZone) snappedPosition = whole + 1;
        else snappedPosition = whole + (fraction - snapZone) / (1 - 2 * snapZone);
        snappedPosition = Math.min(this.faces.length - 1, snappedPosition);
        const targetAngle = snappedPosition * 360 / this.faces.length;
        const elapsed = this.lastFrameTime == null ? 16.67 : Math.min(64, now - this.lastFrameTime);
        this.lastFrameTime = now;
        // Short, time-based smoothing fills wheel-event gaps without dead zones.
        this.displayAngle += (targetAngle - this.displayAngle) * (1 - Math.exp(-elapsed / 70));
        const settled = Math.abs(targetAngle - this.displayAngle) < .01;
        if (settled) this.displayAngle = targetAngle;
        this.hand(this.displayAngle);
        // Do not hide a product link while it has keyboard focus. The hand
        // still follows scrolling; the product catches up on focusout.
        if (!this.items.some(item => item.contains(document.activeElement))) {
          this.show(Math.round(this.displayAngle / (360 / this.faces.length)));
        }
        if (!settled) this.frame = requestAnimationFrame(this.update);
        else this.lastFrameTime = null;
      };
      this.schedule = () => { if (this.frame == null) this.frame = requestAnimationFrame(this.update); };
      this.mode = () => {
        this.scrolling = this.dataset.scroll === 'true' && !this.motion.matches && this.faces.length > 1;
        this.classList.toggle('is-scroll', this.scrolling);
        const headers = [...document.querySelectorAll('#hd, sticky-header, .shopify-section-header, .header-wrapper')];
        const headerHeight = headers.reduce((height, header) => {
          const rect = header.getBoundingClientRect();
          let pinned = false;
          for (let node = header; node && node !== document.body; node = node.parentElement) {
            const position = getComputedStyle(node).position;
            if (position === 'sticky' || position === 'fixed') { pinned = true; break; }
          }
          return pinned && rect.height > 0 ? Math.max(height, rect.height) : height;
        }, 0);
        this.stickyTop = Math.ceil(headerHeight);
        const available = Math.max(200, innerHeight - this.stickyTop);
        this.style.setProperty('--wi-sticky-top', `${this.stickyTop}px`);
        this.style.setProperty('--wi-available', `${available}px`);
        if (matchMedia('(max-width:900px)').matches && this.scrolling) {
          const headingHeight = this.querySelector('.wtDial__head').offsetHeight;
          const infoHeight = this.querySelector('.wtInfo').offsetHeight;
          const diameter = Math.min(this.clientWidth * .84, Math.max(140, available - headingHeight - infoHeight - 60), 520);
          this.clock.style.setProperty('--D', `${diameter}px`);
        } else {
          this.clock.style.removeProperty('--D');
        }
        this.schedule();
      };
      this.faces.forEach((face, i) => {
        face.addEventListener('click', () => this.select(i), {signal});
        face.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (i + 1) % this.faces.length;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (i - 1 + this.faces.length) % this.faces.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.faces.length - 1;
          if (next == null) return;
          event.preventDefault();
          this.select(next);
          this.faces[next].focus({preventScroll:true});
        }, {signal});
      });
      document.addEventListener('shopify:block:select', event => {
        const index = this.faces.findIndex(face => face.dataset.block === event.detail.blockId);
        if (index >= 0) this.select(index);
      }, {signal});
      // Pause only for keyboard focus; pointer focus must not freeze scroll selection.
      document.addEventListener('keydown', event => {
        if (['Tab', 'ArrowRight', 'ArrowLeft', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) this.keyboardNavigation = true;
      }, {capture: true, signal});
      const resumeScroll = () => {
        this.keyboardNavigation = false;
        this.schedule();
      };
      window.addEventListener('wheel', resumeScroll, {passive: true, signal});
      window.addEventListener('touchstart', resumeScroll, {passive: true, signal});
      window.addEventListener('pointerdown', () => { this.keyboardNavigation = false; }, {passive: true, signal});
      window.addEventListener('scroll', this.schedule, {passive:true, signal});
      window.addEventListener('resize', this.mode, {signal});
      this.motion.addEventListener('change', this.mode, {signal});
      this.addEventListener('focusout', this.schedule, {signal});
      this.resizeObserver = new ResizeObserver(this.mode);
      this.resizeObserver.observe(this.querySelector('.wtInfo'));
      this.resizeObserver.observe(this.querySelector('.wtDial__head'));
      this.resizeObserver.observe(this);
      document.querySelectorAll('#hd, sticky-header, .shopify-section-header').forEach(header => this.resizeObserver.observe(header));
      this.show(0);
      this.hand(0);
      this.mode();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
  });
}
